import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { fetchAuditLog } from "../../../Actions/KnowledgeBaseBuilder";
import ArrowLeftSVG from "../../../Assets/svg/ArrowLeftSVG.svg";
import TableLoader from "../../Common/TableLoader";
import { ClipboardList } from "lucide-react";
import { getCached, setCached } from "../../../Utils/fileCache";

const AUDIT_CACHE_KEY = (page) => `audit-log-p${page}`;

const ACTION_STYLES = {
  CREATE: "bg-green-50 text-green-700",
  UPDATE: "bg-blue-50 text-blue-700",
  DELETE: "bg-red-50 text-red-600",
  PERMISSION_CHANGE: "bg-amber-50 text-amber-700",
  BAN: "bg-red-50 text-red-600",
  UNBAN: "bg-gray-100 text-gray-600",
};

class AuditLog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      logs: [],
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      itemsPerPage: null,
      pageInput: "1",
      loading: true,
    };
  }

  static propTypes = {
    fetchAuditLog: PropTypes.func.isRequired,
  };

  componentDidMount() {
    this.fetchLogs(1);
  }

  applyResponse = (response, page) => {
    const resultsCount = response.results ? response.results.length : 0;
    const totalCount = response.count || 0;
    let itemsPerPage;
    if (page === 1 || !this.state.itemsPerPage) {
      itemsPerPage = resultsCount > 0 ? resultsCount : 20;
    } else {
      itemsPerPage = this.state.itemsPerPage;
    }
    const totalPages = totalCount > 0
      ? Math.ceil(totalCount / (page === 1 ? itemsPerPage : this.state.itemsPerPage || itemsPerPage))
      : 1;
    this.setState({
      logs: response.results || [],
      currentPage: page,
      totalPages,
      totalCount,
      itemsPerPage: page === 1 ? itemsPerPage : this.state.itemsPerPage,
      loading: false,
    });
  };

  fetchLogs = async (page) => {
    const cacheKey = AUDIT_CACHE_KEY(page);
    const cached = getCached(cacheKey);
    if (cached) {
      this.applyResponse(cached, page);
      // Background refresh
      this.props.fetchAuditLog(page, 20).then((response) => {
        if (response) {
          setCached(cacheKey, response);
          this.applyResponse(response, page);
        }
      }).catch(() => {});
      return;
    }

    this.setState({ loading: true });
    try {
      const response = await this.props.fetchAuditLog(page, 20);
      if (response) {
        setCached(cacheKey, response);
        this.applyResponse(response, page);
      }
    } catch (error) {
      console.error("Error fetching audit log:", error);
      this.setState({ loading: false });
    }
  };

  handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= this.state.totalPages) {
      this.setState({ currentPage: newPage, pageInput: String(newPage) }, () => {
        this.fetchLogs(newPage);
      });
    }
  };

  handlePageInputChange = (e) => {
    this.setState({ pageInput: e.target.value });
  };

  handlePageInputSubmit = (e) => {
    if (e.key === "Enter") {
      const page = parseInt(this.state.pageInput, 10);
      if (!isNaN(page) && page >= 1 && page <= this.state.totalPages) {
        this.handlePageChange(page);
      } else {
        this.setState({ pageInput: String(this.state.currentPage) });
      }
      e.target.blur();
    }
  };

  handlePageInputBlur = () => {
    const page = parseInt(this.state.pageInput, 10);
    if (!isNaN(page) && page >= 1 && page <= this.state.totalPages) {
      this.handlePageChange(page);
    } else {
      this.setState({ pageInput: String(this.state.currentPage) });
    }
  };

  renderPagination = () => {
    const { currentPage, totalPages } = this.state;
    return (
      <div className="flex justify-between items-center mt-2 pt-4">
        <button
          onClick={() => this.handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <img className="w-4 h-4" src={ArrowLeftSVG} alt="Previous" />
          Previous
        </button>
        <div className="flex items-center gap-2 text-sm text-gray-600 font-medium">
          <span>Page</span>
          <input
            type="text"
            value={this.state.pageInput ?? currentPage}
            onChange={this.handlePageInputChange}
            onKeyDown={this.handlePageInputSubmit}
            onBlur={this.handlePageInputBlur}
            onFocus={(e) => e.target.select()}
            className="w-12 h-9 text-center border border-gray-200 rounded-lg text-sm font-medium text-gray-900 outline-none focus:border-primary-blue transition-colors"
          />
          <span>of {totalPages}</span>
        </div>
        <button
          onClick={() => this.handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Next
          <img className="rotate-180 w-4 h-4" src={ArrowLeftSVG} alt="Next" />
        </button>
      </div>
    );
  };

  render() {
    const { logs, loading } = this.state;

    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 bg-white border border-blue-100 rounded-xl flex flex-col">
          {/* Title bar — aligned with UploadFiles (Knowledge Base) */}
          <div className="px-6 py-4 border-b border-blue-100 rounded-t-xl" style={{ background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)" }}>
            <p className="font-inter font-semibold text-sm text-gray-700 uppercase tracking-wider">
              Audit Log ({this.state.totalCount ?? 0})
            </p>
          </div>
          {/* Column headers */}
          <div className="grid grid-cols-[1.1fr_1.3fr_1fr_0.5fr_1.2fr_0.8fr] items-center py-2 bg-gray-50/50 px-5 gap-4 border-b border-gray-100">
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Timestamp</div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Performed By</div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Action</div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Type</div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Record</div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Department</div>
          </div>

          {/* Rows */}
          {loading ? (
            <TableLoader />
          ) : logs.length > 0 ? (
            logs.map((log) => (
              <div
                key={log.id}
                className="grid grid-cols-[1.1fr_1.3fr_1fr_0.5fr_1.2fr_0.8fr] py-3.5 px-5 gap-4 border-t border-gray-100 font-inter text-sm text-gray-700 hover:bg-blue-50/40 transition-colors items-center"
              >
                <div className="text-gray-500 truncate">
                  {new Date(log.timestamp).toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
                <div className="font-medium text-gray-900 truncate">{log.user}</div>
                <div className="flex items-center">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${ACTION_STYLES[log.action] || "bg-gray-100 text-gray-600"}`}>
                    {log.action}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${log.record_type === "USER" ? "bg-purple-50 text-purple-700" : "bg-gray-100 text-gray-600"}`}>
                    {log.record_type || "FILE"}
                  </span>
                </div>
                <div className="relative group cursor-default truncate">
                  <span>{log.record_name}</span>
                  <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded-md py-1.5 px-3 whitespace-nowrap z-50 shadow-lg">
                    {log.record_name}
                  </div>
                </div>
                <div className="text-gray-500 truncate">{log.department_name || "—"}</div>
              </div>
            ))
          ) : (
            <div className="flex flex-1 w-full flex-col items-center justify-center gap-6 py-12">
              <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)" }}>
                <ClipboardList className="w-10 h-10 text-blue-400" />
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-600">No audit logs yet</p>
                <p className="text-sm text-gray-400 mt-1">
                  Actions on files and users will appear here
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="mt-auto pt-4 pb-6">
          {this.renderPagination()}
        </div>
      </div>
    );
  }
}

export default connect(null, { fetchAuditLog })(AuditLog);
